import React, { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { RotateCcw, AlertTriangle } from 'lucide-react';
import PageShell from '../components/layout/PageShell';
import { useChat } from '../hooks/useChat';
import ReactMarkdown from 'react-markdown';
import { useChatContext } from '../context/ChatContext';
import remarkFlexibleMarkers from 'remark-flexible-markers';

const s = {
  chatWindow: {
    border: '1px solid #e5e5e5', borderRadius: '8px',
    height: '440px', overflowY: 'auto',
    padding: '16px', marginBottom: '12px',
    display: 'flex', flexDirection: 'column', gap: '12px',
    background: '#fafafa',
  },
  bubble: (role) => ({
    maxWidth: '75%', padding: '10px 14px',
    borderRadius: role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
    background: role === 'user' ? '#fd5f0d' : '#fff',
    color: role === 'user' ? '#fff' : '#1a1a1a',
    fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap',
    alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
    border: role === 'assistant' ? '1px solid #e5e5e5' : 'none',
  }),
  typing: {
    alignSelf: 'flex-start', padding: '10px 14px', borderRadius: '12px 12px 12px 2px',
    background: '#fff', border: '1px solid #e5e5e5', fontSize: '18px', letterSpacing: '2px',
  },
  inputRow: { display: 'flex', gap: '8px' },
  input: { flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' },
  sendBtn: (dis) => ({
    padding: '10px 20px', background: dis ? '#fcb185' : '#fd5f0d', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
    cursor: dis ? 'not-allowed' : 'pointer',
  }),
  resetBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    marginTop: '10px', background: 'none', border: 'none', fontSize: '12px',
    color: '#aaa', cursor: 'pointer', padding: 0,
  },
};

export default function AskQuestion() {
  const bottomRef = useRef(null);

  const { messages, input, setInput, loading, sendMessage, handleKey, reset } = useChatContext();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  return (
    <PageShell title="Ask a Question" desc="Chat with the RAAH AI assistant to get answers or get routed.">
      <div style={s.chatWindow}>
        {messages.map((m, i) => (
          <div key={i} style={s.bubble(m.role)}>
            {m.isError && <AlertTriangle size={14} style={{ color: '#c92a2a', marginBottom: '4px' }} />}
            <ReactMarkdown
              remarkPlugins={[remarkFlexibleMarkers]}
              components={{
                mark: ({ children }) => (
                  <mark style={{ background: '#fff176', borderRadius: '3px', padding: '0 2px' }}>
                    {children}
                  </mark>
                ),
                a: ({ href, children }) => (
                  <a     
                    href={href}
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {m.text}
            </ReactMarkdown>
          </div>
        ))}
        {loading && <div style={s.typing}>•••</div>}
        <div ref={bottomRef} />
      </div>

      <div style={s.inputRow}>
        <input
          style={s.input}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Type your question…"
          disabled={loading}
        />
        <button style={s.sendBtn(loading)} onClick={() => sendMessage()} disabled={loading}>
          Send
        </button>
      </div>

      <button style={s.resetBtn} onClick={reset}><RotateCcw size={12} /> Clear conversation</button>
    </PageShell>
  );
}